import { useState } from "react";
import { Link } from "react-router-dom";
import { applyForCreator } from "../api.js";

export default function Apply() {
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [requestedUsername, setRequestedUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [minecraftUsername, setMinecraftUsername] = useState("");
  const [discordHandle, setDiscordHandle] = useState("");
  const [reason, setReason] = useState("");
  const [otherLinks, setOtherLinks] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await applyForCreator({
        requestedUsername: requestedUsername.trim(),
        displayName: displayName.trim(),
        email: email.trim(),
        youtubeUrl: youtubeUrl.trim() || undefined,
        minecraftUsername: minecraftUsername.trim() || undefined,
        discordHandle: discordHandle.trim() || undefined,
        reason: reason.trim(),
        otherLinks: otherLinks.trim() || undefined,
        additionalInfo: additionalInfo.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit application");
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <section className="form-page">
        <h1>Application submitted</h1>
        <p className="form-hint">
          Thanks — your application is now <strong>Pending Review</strong>. An admin will
          reach out on Discord to verify a few details before your account is activated.
          You'll be able to <Link to="/login">log in</Link> once it's approved and an admin
          has set you up with a password.
        </p>
      </section>
    );
  }

  return (
    <section className="form-page">
      <h1>Apply for a creator account</h1>
      <p className="form-hint">
        No password or personal documents needed here — just tell us who you are and where
        to find your existing content. An admin will manually verify your details (usually
        over Discord) before approving anything.
      </p>
      <form onSubmit={onSubmit}>
        <label>
          Requested username
          <input
            value={requestedUsername}
            onChange={(e) => setRequestedUsername(e.target.value)}
            placeholder="3-32 letters, digits, _ or -"
            required
            disabled={busy}
          />
        </label>
        <label>
          Display name
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            disabled={busy}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={busy}
          />
        </label>
        <label>
          YouTube channel URL (optional)
          <input
            type="url"
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="https://youtube.com/@yourchannel"
            disabled={busy}
          />
        </label>
        <label>
          Minecraft username (optional)
          <input
            value={minecraftUsername}
            onChange={(e) => setMinecraftUsername(e.target.value)}
            disabled={busy}
          />
        </label>
        <label>
          Discord username or ID (optional)
          <input
            value={discordHandle}
            onChange={(e) => setDiscordHandle(e.target.value)}
            placeholder="Needed for manual verification"
            disabled={busy}
          />
        </label>
        <label>
          Why do you want a creator account?
          <textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            minLength={20}
            disabled={busy}
          />
        </label>
        <label>
          Links to other public channels (optional)
          <textarea
            rows={2}
            value={otherLinks}
            onChange={(e) => setOtherLinks(e.target.value)}
            disabled={busy}
          />
        </label>
        <label>
          Anything else? (optional)
          <textarea
            rows={3}
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            disabled={busy}
          />
        </label>

        {error ? <p className="form-error">{error}</p> : null}

        <button type="submit" className="button primary" disabled={busy}>
          {busy ? "Submitting…" : "Submit application"}
        </button>
      </form>
    </section>
  );
}
